import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useModal } from '../contexts/ModalContext';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { soundPlayer } from '../utils/audio';
import { 
    ArrowLeft, 
    User, 
    Shield, 
    Bell, 
    Volume2, 
    VolumeX, 
    Moon, 
    Sun, 
    Copy, 
    Check, 
    LogOut, 
    UserX, 
    CheckCircle2, 
    AlertCircle,
    RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const SettingsPage = ({ navigate }) => {
    const { 
        currentUser, 
        userProfile, 
        updateProfileDetails, 
        updateUsername, 
        checkUsernameAvailable, 
        unblockUser,
        signOut 
    } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const { openModal } = useModal();

    // Profile form state
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [avatarSeed, setAvatarSeed] = useState('');
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    // Username form state
    const [usernameInput, setUsernameInput] = useState('');
    const [checkingUsername, setCheckingUsername] = useState(false);
    const [usernameStatus, setUsernameStatus] = useState(null); // { available: bool, error: string }
    const [isSavingUsername, setIsSavingUsername] = useState(false);

    // Blocked users data
    const [blockedUsersData, setBlockedUsersData] = useState([]);
    const [loadingBlocked, setLoadingBlocked] = useState(false);

    // Sound toggle state (saved in localStorage)
    const [soundEnabled, setSoundEnabled] = useState(() => {
        return localStorage.getItem('batcheet_sound_enabled') !== 'false';
    });

    // User ID copy state
    const [copiedId, setCopiedId] = useState(false);

    // Initialize state from profile
    useEffect(() => {
        if (userProfile) {
            setDisplayName(userProfile.displayName || '');
            setBio(userProfile.bio || '');
            setUsernameInput(userProfile.username || '');
        }
    }, [userProfile]);

    // Fetch details of blocked users
    useEffect(() => {
        const fetchBlockedProfiles = async () => {
            if (!userProfile?.blockedUsers || userProfile.blockedUsers.length === 0) {
                setBlockedUsersData([]);
                return;
            }
            setLoadingBlocked(true);
            try {
                const promises = userProfile.blockedUsers.map(async (uid) => {
                    const snap = await getDoc(doc(db, 'users', uid));
                    return snap.exists() ? { id: snap.id, ...snap.data() } : { id: uid, displayName: 'User', username: 'user' };
                });
                const results = await Promise.all(promises);
                setBlockedUsersData(results);
            } catch (err) {
                console.error("Error fetching blocked users:", err);
            } finally {
                setLoadingBlocked(false);
            }
        };

        fetchBlockedProfiles();
    }, [userProfile?.blockedUsers]);

    // Username validation with debounce
    useEffect(() => {
        if (!usernameInput || usernameInput === userProfile?.username) {
            setUsernameStatus(null);
            return;
        }

        const timer = setTimeout(async () => {
            setCheckingUsername(true);
            const res = await checkUsernameAvailable(usernameInput);
            setUsernameStatus(res);
            setCheckingUsername(false);
        }, 350);

        return () => clearTimeout(timer);
    }, [usernameInput, userProfile?.username, checkUsernameAvailable]);

    // Save profile details (Name, Bio, Avatar)
    const handleSaveProfile = async (e) => {
        e.preventDefault();
        if (!displayName.trim()) {
            toast.error("Name cannot be empty.");
            return;
        }
        setIsSavingProfile(true);
        try {
            const updates = {
                displayName: displayName.trim(),
                bio: bio.trim()
            };
            if (avatarSeed) {
                updates.photoURL = `https://api.dicebear.com/7.x/bottts/svg?seed=${avatarSeed}`;
            }
            await updateProfileDetails(updates);
            setAvatarSeed('');
        } catch (err) {
            console.error("Save profile error:", err);
            toast.error("Failed to update profile.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    // Save username
    const handleSaveUsername = async (e) => {
        e.preventDefault();
        if (!usernameStatus?.available) return;
        setIsSavingUsername(true);
        try {
            await updateUsername(usernameInput);
            setUsernameStatus(null);
        } catch (err) {
            console.error("Save username error:", err);
        } finally {
            setIsSavingUsername(false);
        }
    };

    // Toggle sound preference
    const handleToggleSound = () => {
        const next = !soundEnabled;
        setSoundEnabled(next);
        localStorage.setItem('batcheet_sound_enabled', next ? 'true' : 'false');
        if (next) {
            soundPlayer.playNotificationSound();
            toast.success("Notification sounds enabled 🔔");
        } else {
            toast("Notification sounds muted 🔕");
        }
    };

    // Test sound chime
    const handleTestSound = () => {
        soundPlayer.playNotificationSound();
        toast.success("Playing notification chime!");
    };

    // Copy user ID
    const handleCopyId = () => {
        if (!currentUser) return;
        navigator.clipboard.writeText(currentUser.uid);
        setCopiedId(true);
        toast.success("User ID copied to clipboard! 📋");
        setTimeout(() => setCopiedId(false), 2000);
    };

    // Unblock with 2-click confirmation
    const handleUnblockConfirm = (user) => {
        openModal('confirm', {
            title: 'Unblock User',
            message: `Are you sure you want to unblock @${user.username || 'this user'}? You will be able to see their thoughts again.`,
            confirmText: 'Unblock',
            confirmColor: 'blue',
            onConfirm: () => unblockUser(user.id)
        });
    };

    // Sign out with 2-click confirmation
    const handleSignOutConfirm = () => {
        openModal('confirm', {
            title: 'Sign Out',
            message: 'Are you sure you want to sign out of your Batcheet account?',
            confirmText: 'Sign Out',
            confirmColor: 'red',
            onConfirm: () => signOut()
        });
    };

    const currentAvatar = avatarSeed
        ? `https://api.dicebear.com/7.x/bottts/svg?seed=${avatarSeed}`
        : userProfile?.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${currentUser?.uid}`;

    return (
        <div className="space-y-5 animate-fade-in pb-20 max-w-xl mx-auto">
            {/* Top Navigation */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate('profile', { userId: currentUser?.uid })}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors p-1"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Profile</span>
                </button>
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                    Settings & Privacy
                </span>
            </div>

            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                Settings
            </h1>

            {/* 1. Account & Profile Info */}
            <div className="bg-white dark:bg-[#111827]/85 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xs space-y-4">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm">
                    <User className="w-4 h-4 text-sky-500" />
                    <span>Edit Profile</span>
                </div>

                {/* Avatar Preview & Randomizer */}
                <div className="flex items-center gap-3.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/70 dark:border-slate-800/70">
                    <div className="w-14 h-14 rounded-full p-[2px] bg-gradient-to-tr from-sky-400 to-indigo-500 shadow-xs flex-shrink-0">
                        <img
                            src={currentAvatar}
                            alt="Avatar"
                            className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-900 dark:text-slate-100">Profile Avatar</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Unique generative avatar style</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setAvatarSeed('seed_' + Math.random().toString(36).substring(2, 9))}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-sky-500 flex items-center gap-1.5 transition-all active:scale-95 shadow-xs"
                    >
                        <RefreshCw className="w-3.5 h-3.5 text-sky-500" />
                        <span>Randomize</span>
                    </button>
                </div>

                <form onSubmit={handleSaveProfile} className="space-y-3">
                    <div>
                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                            Display Name
                        </label>
                        <input
                            type="text"
                            maxLength={35}
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Your Name"
                            className="w-full px-3.5 py-2 sm:py-2.5 bg-slate-50 dark:bg-slate-900/90 border border-slate-200/90 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                        />
                    </div>

                    <div>
                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                            Bio
                        </label>
                        <textarea
                            rows={2}
                            maxLength={160}
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            placeholder="A short thought about yourself..."
                            className="w-full px-3.5 py-2 sm:py-2.5 bg-slate-50 dark:bg-slate-900/90 border border-slate-200/90 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 resize-none"
                        />
                        <div className="flex justify-end">
                            <span className="text-[10px] text-slate-400 font-mono">{bio.length}/160</span>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isSavingProfile}
                            className="px-4 py-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-bold shadow-xs hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-95 disabled:opacity-50 transition-all flex items-center gap-1.5"
                        >
                            {isSavingProfile ? <span>Saving...</span> : <span>Save Profile</span>}
                        </button>
                    </div>
                </form>

                {/* Username Change Section */}
                <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80">
                    <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">
                        Change Username
                    </label>
                    <form onSubmit={handleSaveUsername} className="space-y-2">
                        <div className="relative">
                            <span className="absolute left-3.5 top-2.5 sm:top-3 text-xs font-mono font-bold text-sky-500">@</span>
                            <input
                                type="text"
                                maxLength={20}
                                value={usernameInput}
                                onChange={(e) => setUsernameInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                placeholder="new_username"
                                className="w-full pl-8 pr-20 py-2 sm:py-2.5 bg-slate-50 dark:bg-slate-900/90 border border-slate-200/90 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                            />
                            <div className="absolute right-2.5 top-2 sm:top-2.5 flex items-center gap-1.5">
                                {checkingUsername ? (
                                    <span className="text-[10px] text-slate-400 animate-pulse">Checking...</span>
                                ) : usernameStatus?.available ? (
                                    <span className="text-xs text-emerald-500 font-bold flex items-center gap-0.5">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Available
                                    </span>
                                ) : usernameStatus?.error ? (
                                    <span className="text-[10px] text-rose-500 font-medium">Taken</span>
                                ) : null}
                            </div>
                        </div>

                        {usernameStatus?.error && (
                            <p className="text-[11px] text-rose-500 font-medium flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {usernameStatus.error}
                            </p>
                        )}

                        {usernameInput !== userProfile?.username && usernameStatus?.available && (
                            <button
                                type="submit"
                                disabled={isSavingUsername}
                                className="px-3.5 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold transition-all shadow-xs"
                            >
                                {isSavingUsername ? 'Saving...' : `Claim @${usernameInput}`}
                            </button>
                        )}
                    </form>
                </div>
            </div>

            {/* 2. Privacy & Blocked Users */}
            <div className="bg-white dark:bg-[#111827]/85 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xs space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm">
                        <Shield className="w-4 h-4 text-rose-500" />
                        <span>Blocked People</span>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 font-mono">
                        {blockedUsersData.length} blocked
                    </span>
                </div>

                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Blocked users cannot view your thoughts, send you messages, or send friend requests.
                </p>

                {loadingBlocked ? (
                    <div className="py-6 text-center text-xs text-slate-400">Loading blocked list...</div>
                ) : blockedUsersData.length > 0 ? (
                    <div className="space-y-2">
                        {blockedUsersData.map(u => (
                            <div
                                key={u.id}
                                className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <img
                                        src={u.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${u.id}`}
                                        alt=""
                                        className="w-9 h-9 rounded-full object-cover bg-slate-200 dark:bg-slate-800"
                                    />
                                    <div className="min-w-0">
                                        <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                                            {u.displayName || 'User'}
                                        </h4>
                                        <p className="text-[10px] text-slate-400 font-mono truncate">
                                            @{u.username || 'user'}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleUnblockConfirm(u)}
                                    className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 shadow-xs transition-all active:scale-95 flex items-center gap-1"
                                >
                                    <UserX className="w-3.5 h-3.5 text-rose-500" />
                                    <span>Unblock</span>
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-6 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-4">
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            No blocked users. Your feed is open and clear.
                        </p>

                    </div>
                )}
            </div>

            {/* 3. Audio & Notifications Preferences */}
            <div className="bg-white dark:bg-[#111827]/85 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xs space-y-4">
                <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-sm">
                    <Bell className="w-4 h-4 text-indigo-500" />
                    <span>Notifications & Sound</span>
                </div>

                <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${soundEnabled ? 'bg-sky-500/10 text-sky-500' : 'bg-slate-200 dark:bg-slate-800 text-slate-400'}`}>
                            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-900 dark:text-slate-100">Message Chime Sound</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">Play soft audio chime when receiving messages</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {soundEnabled && (
                            <button
                                onClick={handleTestSound}
                                className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-sky-500 hover:bg-sky-500/10 transition-colors"
                            >
                                Test
                            </button>
                        )}
                        <button
                            onClick={handleToggleSound}
                            className={`w-11 h-6 rounded-full p-1 transition-colors ${soundEnabled ? 'bg-sky-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${soundEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>
                </div>

                {/* Appearance Theme */}
                <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-amber-500/10 text-amber-500 dark:text-amber-400">
                            {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-900 dark:text-slate-100">Appearance</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">{isDark ? 'Dark Mode' : 'Light Mode'}</p>
                        </div>
                    </div>

                    <button
                        onClick={toggleTheme}
                        className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 shadow-xs hover:border-sky-500 transition-all"
                    >
                        {isDark ? 'Switch to Light' : 'Switch to Dark'}
                    </button>
                </div>
            </div>

            {/* 4. Account Details & Sign Out */}
            <div className="bg-white dark:bg-[#111827]/85 backdrop-blur-md border border-slate-200/90 dark:border-slate-800/80 rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-xs space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Account Security
                </h3>

                <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80">
                        <span className="text-slate-500 dark:text-slate-400">Email</span>
                        <span className="font-semibold text-slate-900 dark:text-slate-200">{currentUser?.email || 'None'}</span>
                    </div>

                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80">
                        <span className="text-slate-500 dark:text-slate-400">User ID</span>
                        <div className="flex items-center gap-1.5 font-mono text-slate-700 dark:text-slate-300">
                            <span className="truncate max-w-[140px] sm:max-w-[200px]">{currentUser?.uid}</span>
                            <button
                                onClick={handleCopyId}
                                className="p-1 text-slate-400 hover:text-sky-500 transition-colors"
                                title="Copy ID"
                            >
                                {copiedId ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="pt-2">
                    <button
                        onClick={handleSignOutConfirm}
                        className="w-full py-2.5 px-4 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/10 dark:hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 text-xs font-bold flex items-center justify-center gap-2 border border-rose-200/80 dark:border-rose-500/20 transition-all active:scale-[0.99]"
                    >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out of Batcheet</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
